import {useDeleteCategoryMutation} from '@transactions/graphql';
import * as React from 'react';
import {removeCategoryFromGroups} from 'src/apolloCache/categoryGroups';
import {removeCategory} from 'src/apolloCache/updateCategories';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {FullCategoryFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type DeleteCategoryModalUserProps = {
  category: FullCategoryFragment;
  onDeleted?: () => void;
};

export const DeleteCategoryModal = (
  props: DeleteCategoryModalUserProps & SystemProps,
) => {
  const {category, onRequestClose, onDeleted} = props;
  const [deleteCategory, {loading}] = useDeleteCategoryMutation();
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteCategory({
        variables: {id: category._id},
        update: (store) => {
          removeCategory(store, category);
          removeCategoryFromGroups(store, category._id);
        },
      });
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? 'Deleting...' : 'Delete'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Delete category"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to delete <strong>{category.name}</strong>? This
        action cannot be undone.
      </p>
    </Modal>
  );
};
